import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS } from "@/lib/site";
import LogoMark from "@/components/LogoMark";

const Logo = () => (
  <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2.5">
    <LogoMark className="h-8 w-8 text-white transition-transform duration-500 group-hover:rotate-90" />
    <span className="font-display text-[15px] font-semibold tracking-tight text-white">
      Core Loop<span className="text-cyber">.</span>
    </span>
  </Link>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed left-0 right-0 top-0 z-50 transition-[background,border-color,padding] duration-500 ${
        scrolled ? "border-b border-white/10 bg-ink-950/80 py-3 backdrop-blur-xl" : "border-b border-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1400px] items-center justify-between px-6 lg:px-10">
        <Logo />

        <div className="hidden items-center gap-9 md:flex">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `group relative font-body text-sm transition-colors duration-300 ${
                  isActive ? "text-white" : "text-white/55 hover:text-white"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-cyber transition-all duration-400 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                    aria-hidden="true"
                  />
                </>
              )}
            </NavLink>
          ))}
        </div>

        <Link
          to="/contact"
          data-testid="nav-cta"
          className="group hidden items-center gap-2 rounded-full border border-cyber/40 bg-cyber/5 px-5 py-2 font-body text-sm text-cyber transition-colors duration-300 hover:bg-cyber hover:text-ink-950 md:inline-flex"
        >
          Start a project
          <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-white md:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-ink-950 md:hidden"
            data-testid="nav-mobile-menu"
          >
            <div className="flex flex-col gap-1 px-6 py-5">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  className="border-b border-white/5 py-3 font-display text-2xl text-white/80"
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                className="mt-4 inline-flex w-max items-center gap-2 rounded-full bg-cyber px-5 py-2.5 font-body text-sm font-medium text-ink-950"
              >
                Start a project <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
